import * as React from 'react';
import { connect } from 'redux-scaffolding-ts';
import { OemDto, OemsStore } from '../../stores/configuration/machinery/oems-store';
import { nameof } from '../../utils/object';
import { ItemReference } from '../../stores/dataStore';
import { isNullOrWhiteSpaces } from '../../utils/useful-functions';
import { PatternsStore, PatternDto } from 'stores/configuration/machinery/patterns-store';
import { useCallback, useEffect, useState } from 'react';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

interface TailwindPatternEditorProps {
  value: ItemReference;
  onChange?: (value: ItemReference) => void;
  placeholder?: string;
  patterns?: OemsStore;
  nullable?: boolean;
  minWidth?: number;
  reloadOnChange?: boolean;
  className?: string;
  showOnlyActive?: boolean;
  clearable?: boolean;
  readOnly?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindPatternEditor: React.FC<TailwindPatternEditorProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  nullable = false,
  readOnly = false,
  clearable = true,
  patterns,
  minWidth,
  reloadOnChange,
  showOnlyActive,
  loadDataOnOpen = false,
  onFocus
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getPatternMethod = useCallback(
    async (search: string = '') => {
      try {
        setIsLoading(true);
        const filter = isNullOrWhiteSpaces(search) ? [] : [`startswith(tolower(${nameof<PatternDto>('name')}), '${search.toLowerCase()}')`];
        if (showOnlyActive) filter.push('active eq true');
        const result = await patterns.getAllAsync(
          {
            searchQuery: '',
            skip: 0,
            take: 100000,
            orderBy: [{ direction: 'Ascending', field: nameof<OemDto>('name'), useProfile: false }],
            filter: filter,
            select: 'id, name'
          },
          search
        );
        setOptions(
          result.items.map(item => ({
            value: item.id,
            label: item.name
          }))
        );
      } catch (error) {
        console.error('Error loading locations:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [patterns, showOnlyActive] //Si falla es porque posteriormente a la limpieza, se ha añadido showOnlyActive al array de dependencias
  );

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getPatternMethod();
    }
  }, [loadDataOnOpen, options.length, getPatternMethod]);

  useEffect(() => {
    if (!loadDataOnOpen || (value && options.length === 0)) {
      getPatternMethod();
    }
  }, [loadDataOnOpen, value, options.length, getPatternMethod]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleChange = (newValue: string | null) => {
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;

    const itemRef = selected ? { id: selected.value, title: selected.label } : null;

    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(delivery => ({
              id: delivery.value,
              text: delivery.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      disabled={readOnly}
      className="w-full"
      onOpenDropDown={onOpenDropDown}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['patterns', PatternsStore])(TailwindPatternEditor);
